import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    CircularProgress,
    Chip,
    Card,
    CardHeader,
    CardContent,
    TextField,
    Stack,
    Switch,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { RefundType } from "../../models/refundType";
import { FullProperty, PaymentEvent } from "../../models/fullProperty";
import DownloadIcon from '@mui/icons-material/Download';
import { downloadPayoutsCSV, fetchPayouts } from "../../dashboard/billing/billingServices/payouts";
import { defaultSummaryData, Payout, Summary } from "../../dashboard/billing/billingData";
import { restaurantService } from "../../services/firebase/restaurantManagementService";
import { refundService } from "../../services/firebase/refundService";
import { formatMoney } from "../../services/strings/formatters";

export interface ManageSpecificRestaurantPageProps {
    setRestaurant: React.Dispatch<React.SetStateAction<FullProperty | undefined>>;
    restaurant: FullProperty;
    setRestaurantRefunds: React.Dispatch<React.SetStateAction<RefundType[] | undefined>>;
    refunds: RefundType[];
}

const calculateAmountDue = (restaurant: FullProperty) => {
    const netSales = restaurant.totalSalesInZarC - restaurant.totalRefundsInZarC;
    const totalPaid = restaurant.paymentHistory.reduce(
        (sum: number, evt: PaymentEvent) => sum + evt.amount,
        0
    );
    return Math.max(netSales - totalPaid, 0);
};

const exportPayoutsCSV = async (payouts: Payout[]) => {
    downloadPayoutsCSV(payouts);
};

const getNextPaymentDate = (paymentHistory: PaymentEvent[]): string => {
    if (paymentHistory.length === 0) return "-";
    const lastDate = paymentHistory
        .map((evt) => new Date(evt.paymentDate))
        .sort((a, b) => b.getTime() - a.getTime())[0];
    const nextDue = new Date(lastDate);
    nextDue.setDate(nextDue.getDate() + 30);
    return format(nextDue, "dd MMM yyyy");
};

export const ManageSpecificRestaurantPage: React.FC<ManageSpecificRestaurantPageProps> = ({
    restaurant,
    refunds,
    setRestaurant,
    setRestaurantRefunds,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [payouts, setPayouts] = useState<Array<Payout>>([]);
    const [summaryData, setSummaryData] = useState<Summary>(defaultSummaryData);
    const [newPaymentAmount, setNewPaymentAmount] = useState<string>("");
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [isPlantBasedTreaty, setIsPlantBasedTreaty] = useState<boolean>(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchBilling = async () => {
            if (!restaurant) return;
            setPayouts(fetchPayouts(restaurant));
            const { lifetimePaid, lifetimeValue, nextPayment } =
                restaurantService.getFinancialSummary(restaurant);
            setSummaryData({
                ...summaryData,
                pendingPayout: nextPayment,
                totalEarnings: lifetimeValue,
                totalPaid: lifetimePaid,
            });
            const newIsPlantBasedTreaty = restaurant.isPlantBasedTreaty ?? false;
            setIsPlantBasedTreaty(newIsPlantBasedTreaty);
        };
        const newIsPlantBasedTreaty = restaurant.isPlantBasedTreaty ?? false;
        setIsPlantBasedTreaty(newIsPlantBasedTreaty);
        fetchBilling();
    }, [restaurant]);



    const handleAddPayment = async () => {
        if (!newPaymentAmount) return;
        const cents = Math.round(parseFloat(newPaymentAmount) * 100);
        setIsAdding(true);
        try {
            // update backend
            await restaurantService.addPaymentToRestaurant(restaurant,
                new Date().toISOString(),
                cents,
            );
            // refresh restaurant & payouts
            const updated = await restaurantService.fetchRestaurantById(restaurant.id);
            setRestaurant(updated);
            setPayouts(fetchPayouts(updated));
            setNewPaymentAmount("");
        } catch (err) {
            setError("Failed to add payment. Please try again.");
        } finally {
            setIsAdding(false);
        }
    };

    const handleSubmitPbt = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await restaurantService.changeRestaurantPlantBasedTreatyStatus(restaurant.id, isPlantBasedTreaty ?? false);
            // Optionally show a snackbar/toast here
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" align="center" sx={{ mt: 4 }}>
                {error}
            </Typography>
        );
    }

    const nextDue = getNextPaymentDate(restaurant.paymentHistory);

    return (
        <Box sx={{ p: 3 }} className="patlite">
            <Button
                variant="contained"
                size="small"
                onClick={() => {
                    setRestaurant(undefined);
                    setRestaurantRefunds(undefined);
                }}
            >
                Back
            </Button>

            <Typography color="h1" align="center" sx={{ mt: 4 }}>
                Restaurant ID #{restaurant.id}
            </Typography>

            <Card elevation={4} sx={{ mt: 4 }}>
                <CardHeader
                    title={<Typography variant="h6">Payout History</Typography>}
                    action={
                        <Button variant="outlined" onClick={() => exportPayoutsCSV(payouts)}>
                            <DownloadIcon />Export CSV
                        </Button>
                    }
                />
                <CardContent>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ backgroundColor: '#eeeeee' }}>
                                <tr>
                                    {['Date', 'Payout ID', 'Period', 'Amount', 'Status'].map((h) => (
                                        <th
                                            key={h}
                                            style={{ padding: '12px', fontSize: 12, textTransform: 'uppercase', color: '#616161' }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {payouts.map((p) => (
                                    <tr
                                        key={p.id}
                                        style={{ transition: 'background-color 0.2s' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        <td style={{ padding: '12px', fontSize: 14 }}>{p.date}</td>
                                        <td style={{ padding: '12px', fontSize: 14 }}>{p.id}</td>
                                        <td style={{ padding: '12px', fontSize: 14 }}>{`${p.periodStart} to ${p.periodEnd}`}</td>
                                        <td style={{ padding: '12px', fontSize: 14, textAlign: 'right' }}>{formatMoney(p.amount)}</td>
                                        <td style={{ padding: '12px', fontSize: 14, textAlign: 'center' }}>
                                            <Chip
                                                label={p.status}
                                                color={
                                                    p.status === 'Paid'
                                                        ? 'primary'
                                                        : p.status === 'Pending'
                                                            ? 'warning'
                                                            : 'error'
                                                }
                                                size="small"
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Add new payment row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 2 }}>
                        <TextField
                            label="New Payment (£)"
                            variant="outlined"
                            size="small"
                            type="number"
                            value={newPaymentAmount}
                            onChange={(e) => setNewPaymentAmount(e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddPayment}
                            disabled={!newPaymentAmount || isAdding}
                        >
                            {isAdding ? <CircularProgress size={20} /> : 'Add Payment'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
            <Box sx={{ p: 2 }} />
            <Card elevation={4} sx={{ mt: 4 }}>
                <CardHeader
                    title={<Typography variant="h6">Pending Refunds On The Restaurant</Typography>}
                // action={
                //      <Button variant="outlined" onClick={() => exportPayoutsCSV(payouts)}>
                //          <DownloadIcon />Export CSV
                //      </Button>
                // }
                />
                <CardContent>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ backgroundColor: '#eeeeee' }}>
                                <tr>
                                    {['Created At', 'Refund ID', 'Amount', 'Status'].map((h) => (
                                        <th
                                            key={h}
                                            style={{ padding: '12px', fontSize: 12, textTransform: 'uppercase', color: '#616161' }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {refunds.map((r) => (
                                    <tr
                                        key={r.orderId}
                                        style={{ transition: 'background-color 0.2s' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        <td style={{ padding: '12px', fontSize: 14 }}>{r.createdAt}</td>
                                        <td style={{ padding: '12px', fontSize: 14 }}>{r.orderId}</td>
                                        <td style={{ padding: '12px', fontSize: 14 }}>{formatMoney(Math.round(r.refundAmount || 0) / 100)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Add new payment row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, gap: 2 }}>
                        <TextField
                            label="New Payment (£)"
                            variant="outlined"
                            size="small"
                            type="number"
                            value={newPaymentAmount}
                            onChange={(e) => setNewPaymentAmount(e.target.value)}
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddPayment}
                            disabled={!newPaymentAmount || isAdding}
                        >
                            {isAdding ? <CircularProgress size={20} /> : 'Add Payment'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
            <Box sx={{ p: 2 }} />

            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <Typography
                    variant="h6"
                    component="h2"
                    gutterBottom
                    align="center"
                    sx={{ color: 'primary.dark' }}
                >
                    Manage Plant Based Treaty
                </Typography>
                <Box component="form" onSubmit={handleSubmitPbt}>
                    <Stack spacing={2}>
                        <Box key={'pbt'} sx={{ display: 'flex', alignItems: 'center' }}>

                            <Switch
                                checked={isPlantBasedTreaty}
                                onChange={(e) =>
                                    setIsPlantBasedTreaty(!isPlantBasedTreaty)
                                }
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button
                                variant="contained"
                                type="submit"
                                sx={{ textTransform: 'none' }}
                                disabled={loading}
                            >
                                Submit
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

export default ManageSpecificRestaurantPage;
